import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Tooltip, EmptyState } from '../Common/ReusableUI';
import { useToast } from '../Common/ToastProvider';
import { CardSkeleton, TableSkeleton } from '../Common/SkeletonLoaders';
import {
  UtensilsCrossed, Thermometer, AlertTriangle, Printer, Calendar, Clock,
  Search, Filter, ChevronDown, ChevronRight, Bell, CheckCircle, XCircle,
  RefreshCw, FileText, Users, Eye, Edit, Plus, Trash2, Shield,
  Droplets, Star, ArrowRight, TrendingUp, ClipboardCheck, Snowflake, Flame,
  Save, BarChart3
} from 'lucide-react';

// ── IDDSI Reference ──────────────────────────────────────────────
const IDDSI_FOOD_LABELS = {
  0: 'Thin', 1: 'Slightly Thick', 2: 'Mildly Thick', 3: 'Liquidised',
  4: 'Pureed', 5: 'Minced & Moist', 6: 'Soft & Bite-Sized', 7: 'Regular/Easy to Chew'
};
const IDDSI_FLUID_LABELS = {
  0: 'Thin', 1: 'Slightly Thick', 2: 'Mildly Thick', 3: 'Moderately Thick', 4: 'Extremely Thick'
};
const IDDSI_FOOD_TOOLTIPS = {
  0: 'Flows like water — no thickening needed',
  1: 'Thicker than water but still flows — stir easily',
  2: 'Flows off a spoon — coats the mouth',
  3: 'Smooth with no lumps — pours from a cup',
  4: 'Holds its shape — can be eaten with a fork',
  5: 'Soft, moist lumps < 4mm — easily squashed',
  6: 'Pieces < 15mm — tender and moist',
  7: 'Normal everyday food textures'
};
const IDDSI_FLUID_TOOLTIPS = {
  0: 'Flows like water — no thickening needed',
  1: 'Thicker than water but still flows — stir easily',
  2: 'Flows off a spoon — coats the mouth',
  3: 'Holds shape on spoon — doesn\'t flow freely',
  4: 'Holds its shape — can be eaten with a fork'
};
const IDDSI_FOOD_COLORS = {
  0: 'bg-blue-100 text-blue-800', 1: 'bg-gray-100 text-gray-800',
  2: 'bg-pink-100 text-pink-800', 3: 'bg-yellow-100 text-yellow-800',
  4: 'bg-orange-100 text-orange-800', 5: 'bg-red-100 text-red-800',
  6: 'bg-blue-100 text-blue-700', 7: 'bg-green-100 text-green-800'
};
const IDDSI_FLUID_COLORS = {
  0: 'bg-cyan-100 text-cyan-800', 1: 'bg-teal-100 text-teal-800',
  2: 'bg-indigo-100 text-indigo-800', 3: 'bg-violet-100 text-violet-800',
  4: 'bg-fuchsia-100 text-fuchsia-800'
};

const INTAKE_LEVELS = ['Full', '3/4', '1/2', '1/4', 'Refused', 'NPO'];
const INTAKE_STORAGE_KEY = 'kitchen_meal_intake';

function loadMealIntake() {
  try {
    return JSON.parse(localStorage.getItem(INTAKE_STORAGE_KEY) || '{}');
  } catch { return {}; }
}

function saveMealIntake(data) {
  localStorage.setItem(INTAKE_STORAGE_KEY, JSON.stringify(data));
}

// ── Mock Data ────────────────────────────────────────────────────
const MOCK_RESIDENTS = [
  { id: 'R001', name: 'Eleanor Mitchell', room: '12A', wing: 'Banksia', foodLevel: 7, fluidLevel: 0, allergies: ['Shellfish'], preferences: ['Vegetarian option Fridays', 'Likes warm milk before bed'], portionSize: 'Regular', syncStatus: 'synced', lastAssessment: '2026-04-08' },
  { id: 'R002', name: 'Harold Thompson', room: '15B', wing: 'Wattle', foodLevel: 5, fluidLevel: 2, allergies: ['Gluten', 'Dairy'], preferences: ['Extra gravy on meats', 'No capsicum'], portionSize: 'Large', syncStatus: 'synced', lastAssessment: '2026-04-07' },
  { id: 'R003', name: 'Margaret Chen', room: '8C', wing: 'Banksia', foodLevel: 4, fluidLevel: 3, allergies: ['Peanuts', 'Tree Nuts'], preferences: ['Chinese-style meals when available'], portionSize: 'Small', syncStatus: 'pending', lastAssessment: '2026-04-09' },
  { id: 'R004', name: 'Arthur Williams', room: '22A', wing: 'Grevillea', foodLevel: 6, fluidLevel: 1, allergies: [], preferences: ['Strong tea', 'Vegemite toast at breakfast'], portionSize: 'Regular', syncStatus: 'synced', lastAssessment: '2026-04-05' },
  { id: 'R005', name: 'Dorothy Nguyen', room: '3B', wing: 'Wattle', foodLevel: 3, fluidLevel: 4, allergies: ['Eggs', 'Soy'], preferences: ['Vietnamese-style soups preferred'], portionSize: 'Small', syncStatus: 'synced', lastAssessment: '2026-04-06' },
  { id: 'R006', name: 'Frank O\'Brien', room: '19C', wing: 'Grevillea', foodLevel: 7, fluidLevel: 0, allergies: ['Lactose'], preferences: ['Loves lamb roast', 'Beer-battered fish Fridays'], portionSize: 'Large', syncStatus: 'error', lastAssessment: '2026-04-03' },
  { id: 'R007', name: 'Irene Kowalski', room: '6A', wing: 'Banksia', foodLevel: 5, fluidLevel: 2, allergies: ['Sesame'], preferences: ['Polish-style dishes', 'Extra bread with meals'], portionSize: 'Regular', syncStatus: 'synced', lastAssessment: '2026-04-08' },
  { id: 'R008', name: 'George Patel', room: '11B', wing: 'Wattle', foodLevel: 6, fluidLevel: 1, allergies: ['Mustard', 'Celery'], preferences: ['Mild curry options', 'No pork'], portionSize: 'Regular', syncStatus: 'synced', lastAssessment: '2026-04-09' },
  { id: 'R009', name: 'Betty Andersen', room: '25A', wing: 'Grevillea', foodLevel: 4, fluidLevel: 3, allergies: ['Fish'], preferences: ['Scandinavian breakfast', 'Rye bread'], portionSize: 'Small', syncStatus: 'pending', lastAssessment: '2026-04-10' },
  { id: 'R010', name: 'William Tan', room: '7C', wing: 'Banksia', foodLevel: 7, fluidLevel: 0, allergies: [], preferences: ['Double dessert portions', 'Black coffee'], portionSize: 'Large', syncStatus: 'synced', lastAssessment: '2026-04-04' }
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEAL_SLOTS = ['Breakfast', 'Morning Tea', 'Lunch', 'Afternoon Tea', 'Dinner', 'Supper'];

const MOCK_MENU = {
  Monday:    { 'Breakfast': 'Porridge with honey & banana', 'Morning Tea': 'Scones with jam & cream', 'Lunch': 'Roast lamb with vegetables', 'Afternoon Tea': 'Fruit salad & yoghurt', 'Dinner': 'Chicken schnitzel & mash', 'Supper': 'Warm Milo & biscuit' },
  Tuesday:   { 'Breakfast': 'Scrambled eggs on toast', 'Morning Tea': 'Pikelets with butter', 'Lunch': 'Beef & vegetable pie', 'Afternoon Tea': 'Cheese & crackers', 'Dinner': 'Grilled barramundi & chips', 'Supper': 'Custard cup' },
  Wednesday: { 'Breakfast': 'Bircher muesli & fruit', 'Morning Tea': 'Banana bread', 'Lunch': 'Chicken stir-fry with rice', 'Afternoon Tea': 'Lamington slices', 'Dinner': 'Shepherd\'s pie', 'Supper': 'Warm milk & shortbread' },
  Thursday:  { 'Breakfast': 'Pancakes with maple syrup', 'Morning Tea': 'Anzac biscuits', 'Lunch': 'Fish & chips with tartare', 'Afternoon Tea': 'Jelly & ice cream', 'Dinner': 'Roast pork with apple sauce', 'Supper': 'Hot chocolate & toast' },
  Friday:    { 'Breakfast': 'Vegemite toast & cereal', 'Morning Tea': 'Date scones', 'Lunch': 'Meat pie & salad', 'Afternoon Tea': 'Pavlova slice', 'Dinner': 'Beer-battered fish & salad', 'Supper': 'Rice pudding' },
  Saturday:  { 'Breakfast': 'Bacon & eggs with tomato', 'Morning Tea': 'Apple muffins', 'Lunch': 'Roast chicken & gravy', 'Afternoon Tea': 'Tim Tam platter', 'Dinner': 'Spaghetti bolognese', 'Supper': 'Warm Ovaltine & biscuit' },
  Sunday:    { 'Breakfast': 'French toast & berries', 'Morning Tea': 'Fruit cake', 'Lunch': 'Roast beef & Yorkshire pudding', 'Afternoon Tea': 'Scones & tea', 'Dinner': 'Chicken soup & bread rolls', 'Supper': 'Stewed fruit & cream' }
};

const MOCK_ALERTS = [
  { id: 'A001', residentId: 'R001', residentName: 'Eleanor Mitchell', room: '12A', changeType: 'IDDSI Food Level', oldValue: 'Level 5 — Minced & Moist', newValue: 'Level 4 — Pureed', changedBy: 'Dr Sarah Wong (Speech Pathologist)', timestamp: '2026-04-10T09:15:00', urgency: 'high', acknowledged: false },
  { id: 'A002', residentId: 'R003', residentName: 'Margaret Chen', room: '8C', changeType: 'IDDSI Fluid Level', oldValue: 'Level 2 — Mildly Thick', newValue: 'Level 3 — Moderately Thick', changedBy: 'Dr Sarah Wong (Speech Pathologist)', timestamp: '2026-04-10T08:42:00', urgency: 'high', acknowledged: false },
  { id: 'A003', residentId: 'R005', residentName: 'Dorothy Nguyen', room: '3B', changeType: 'New Allergy Added', oldValue: '—', newValue: 'Soy', changedBy: 'Nurse James Park', timestamp: '2026-04-09T16:30:00', urgency: 'critical', acknowledged: true },
  { id: 'A004', residentId: 'R008', residentName: 'George Patel', room: '11B', changeType: 'Portion Size', oldValue: 'Large', newValue: 'Regular', changedBy: 'Dietitian Lisa Monroe', timestamp: '2026-04-09T14:10:00', urgency: 'low', acknowledged: true },
  { id: 'A005', residentId: 'R002', residentName: 'Harold Thompson', room: '15B', changeType: 'Dietary Preference', oldValue: '—', newValue: 'Extra gravy on meats', changedBy: 'Nurse Amy Fletcher', timestamp: '2026-04-09T11:45:00', urgency: 'low', acknowledged: true },
  { id: 'A006', residentId: 'R006', residentName: "Frank O'Brien", room: '19C', changeType: 'IDDSI Food Level', oldValue: 'Level 6 — Soft & Bite-Sized', newValue: 'Level 7 — Regular', changedBy: 'Dr Sarah Wong (Speech Pathologist)', timestamp: '2026-04-08T10:20:00', urgency: 'medium', acknowledged: true },
  { id: 'A007', residentId: 'R009', residentName: 'Betty Andersen', room: '25A', changeType: 'New Allergy Added', oldValue: '—', newValue: 'Fish', changedBy: 'GP Dr Tom Richards', timestamp: '2026-04-08T09:00:00', urgency: 'critical', acknowledged: true }
];

const MOCK_TEMP_LOGS = [
  { id: 'T001', equipment: 'Fridge A (Main Kitchen)', type: 'fridge', temp: 3.2, target: '0–5°C', status: 'pass', recordedBy: 'Chef Kim Nguyen', timestamp: '2026-04-10T06:00:00' },
  { id: 'T002', equipment: 'Fridge B (Dairy)', type: 'fridge', temp: 4.8, target: '0–5°C', status: 'pass', recordedBy: 'Chef Kim Nguyen', timestamp: '2026-04-10T06:05:00' },
  { id: 'T003', equipment: 'Freezer 1', type: 'freezer', temp: -19.5, target: '< −18°C', status: 'pass', recordedBy: 'Chef Kim Nguyen', timestamp: '2026-04-10T06:10:00' },
  { id: 'T004', equipment: 'Freezer 2 (Meats)', type: 'freezer', temp: -15.2, target: '< −18°C', status: 'fail', recordedBy: 'Sous Chef Raj Mehta', timestamp: '2026-04-10T06:12:00' },
  { id: 'T005', equipment: 'Hot Bain-Marie (Lunch)', type: 'hot', temp: 72.0, target: '> 60°C', status: 'pass', recordedBy: 'Sous Chef Raj Mehta', timestamp: '2026-04-10T11:30:00' },
  { id: 'T006', equipment: 'Cold Display (Salads)', type: 'cold', temp: 6.8, target: '< 5°C', status: 'fail', recordedBy: 'Chef Kim Nguyen', timestamp: '2026-04-10T11:35:00' },
  { id: 'T007', equipment: 'Hot Trolley (Banksia)', type: 'hot', temp: 65.3, target: '> 60°C', status: 'pass', recordedBy: 'Sous Chef Raj Mehta', timestamp: '2026-04-10T12:00:00' },
  { id: 'T008', equipment: 'Hot Trolley (Wattle)', type: 'hot', temp: 58.1, target: '> 60°C', status: 'fail', recordedBy: 'Sous Chef Raj Mehta', timestamp: '2026-04-10T12:05:00' }
];

const MOCK_CLEANING = [
  { id: 'CL01', task: 'Bench & prep surface sanitisation', frequency: 'After each service', lastCompleted: '2026-04-10T13:45:00', completedBy: 'Kim Nguyen', status: 'done' },
  { id: 'CL02', task: 'Floor mopping & degreasing', frequency: 'Twice daily', lastCompleted: '2026-04-10T14:00:00', completedBy: 'Raj Mehta', status: 'done' },
  { id: 'CL03', task: 'Deep clean — fryer & oven', frequency: 'Weekly (Wednesday)', lastCompleted: '2026-04-09T16:30:00', completedBy: 'Kim Nguyen', status: 'done' },
  { id: 'CL04', task: 'Fridge & freezer internal wipe-down', frequency: 'Weekly (Monday)', lastCompleted: '2026-04-07T07:00:00', completedBy: 'Raj Mehta', status: 'done' },
  { id: 'CL05', task: 'Pest control inspection', frequency: 'Monthly', lastCompleted: '2026-03-15T10:00:00', completedBy: 'External — PestAway Pty Ltd', status: 'due' },
  { id: 'CL06', task: 'Grease trap service', frequency: 'Quarterly', lastCompleted: '2026-01-20T09:00:00', completedBy: 'External — CleanFlow Services', status: 'overdue' },
  { id: 'CL07', task: 'Exhaust hood & canopy deep clean', frequency: 'Quarterly', lastCompleted: '2026-02-10T08:00:00', completedBy: 'External — HoodClean AU', status: 'due' }
];

const TABS = [
  { key: 'profiles', label: 'Dietary Profiles', icon: Users },
  { key: 'menu', label: 'Weekly Menu', icon: Calendar },
  { key: 'tickets', label: 'Meal Tickets', icon: FileText },
  { key: 'intake', label: 'Meal Intake', icon: BarChart3 },
  { key: 'alerts', label: 'Change Alerts', icon: Bell },
  { key: 'safety', label: 'Food Safety', icon: Shield }
];

// ── Allergy cross-reference engine ───────────────────────────────
function detectAllergenMatches(allergies, menuItem) {
  if (!allergies?.length || !menuItem) return [];
  const itemLower = menuItem.toLowerCase();
  const ALLERGEN_KEYWORDS = {
    'Shellfish': ['shellfish', 'prawn', 'shrimp', 'lobster', 'crab', 'mussel', 'oyster'],
    'Gluten': ['gluten', 'wheat', 'bread', 'toast', 'flour', 'pastry', 'pie', 'cake', 'biscuit', 'scone', 'pikelet', 'pancake', 'muesli', 'cracker', 'schnitzel', 'battered', 'spaghetti', 'pudding', 'lamington', 'anzac', 'shortbread', 'muffin', 'roll'],
    'Dairy': ['dairy', 'milk', 'cream', 'cheese', 'butter', 'yoghurt', 'custard', 'ice cream', 'milo', 'ovaltine', 'chocolate'],
    'Peanuts': ['peanut'],
    'Tree Nuts': ['nut', 'almond', 'walnut', 'cashew', 'pistachio', 'pecan', 'macadamia', 'hazelnut'],
    'Eggs': ['egg', 'scrambled', 'omelette', 'french toast', 'pancake', 'pikelet', 'custard', 'pavlova', 'schnitzel'],
    'Soy': ['soy', 'soya', 'tofu', 'stir-fry'],
    'Lactose': ['milk', 'cream', 'cheese', 'butter', 'yoghurt', 'custard', 'ice cream', 'milo', 'ovaltine', 'chocolate'],
    'Sesame': ['sesame'],
    'Mustard': ['mustard'],
    'Celery': ['celery'],
    'Fish': ['fish', 'barramundi', 'salmon', 'tuna', 'cod', 'tartare']
  };
  const matches = [];
  for (const allergy of allergies) {
    const keywords = ALLERGEN_KEYWORDS[allergy] || [allergy.toLowerCase()];
    for (const kw of keywords) {
      if (itemLower.includes(kw)) {
        matches.push({ allergen: allergy, keyword: kw });
        break;
      }
    }
  }
  return matches;
}

// ── Helper Components ────────────────────────────────────────────
const SyncBadge = ({ status }) => {
  const cfg = {
    synced:  { icon: CheckCircle, cls: 'text-green-600 bg-green-50', label: 'Synced' },
    pending: { icon: RefreshCw,   cls: 'text-amber-600 bg-amber-50', label: 'Pending' },
    error:   { icon: XCircle,     cls: 'text-red-600 bg-red-50',     label: 'Error' }
  }[status] || { icon: RefreshCw, cls: 'text-gray-500 bg-gray-50', label: status };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <Icon className={`w-3 h-3 ${status === 'pending' ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
};

const UrgencyBadge = ({ level }) => {
  const cls = { critical: 'bg-red-600 text-white', high: 'bg-orange-500 text-white', medium: 'bg-yellow-400 text-yellow-900', low: 'bg-gray-200 text-gray-700' }[level] || 'bg-gray-200 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${cls}`}>{level}</span>;
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'purple' }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-start gap-4">
    <div className={`p-3 rounded-xl bg-${color}-100`}>
      <Icon className={`w-5 h-5 text-${color}-600`} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const IddsiLabel = ({ level, type = 'food' }) => {
  const labels = type === 'food' ? IDDSI_FOOD_LABELS : IDDSI_FLUID_LABELS;
  const colors = type === 'food' ? IDDSI_FOOD_COLORS : IDDSI_FLUID_COLORS;
  const tips = type === 'food' ? IDDSI_FOOD_TOOLTIPS : IDDSI_FLUID_TOOLTIPS;
  const prefix = type === 'food' ? 'Food' : 'Fluid';
  return (
    <Tooltip content={tips[level] || 'IDDSI level'} position="top">
      <span className={`px-2 py-0.5 rounded text-xs font-medium cursor-help ${colors[level]}`}>
        {prefix}: {level} — {labels[level]}
      </span>
    </Tooltip>
  );
};

// ── Main Component ───────────────────────────────────────────────
const Kitchen = () => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profiles');
  const [profileSearch, setProfileSearch] = useState('');
  const [profileWingFilter, setProfileWingFilter] = useState('all');
  const [expandedResident, setExpandedResident] = useState(null);
  const [menuEditCell, setMenuEditCell] = useState(null);
  const [menuData, setMenuData] = useState(MOCK_MENU);
  const [menuEditValue, setMenuEditValue] = useState('');
  const [ticketMeal, setTicketMeal] = useState('Lunch');
  const [ticketSearch, setTicketSearch] = useState('');
  const [alerts, setAlerts] = useState(MOCK_ALERTS);
  const [alertFilter, setAlertFilter] = useState('all');
  const [safetySubTab, setSafetySubTab] = useState('temps');

  // Meal intake state
  const [intakeData, setIntakeData] = useState(loadMealIntake);
  const [intakeMeal, setIntakeMeal] = useState('Lunch');
  const [intakeView, setIntakeView] = useState('record'); // 'record' | 'summary'

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Kitchen] mounted, tabs:', TABS.map(t => t.key));
    }
  }, []);

  // Derived data
  const filteredResidents = useMemo(() => {
    let list = MOCK_RESIDENTS;
    if (profileWingFilter !== 'all') list = list.filter(r => r.wing === profileWingFilter);
    if (profileSearch.trim()) {
      const q = profileSearch.toLowerCase();
      list = list.filter(r => r.name.toLowerCase().includes(q) || r.room.toLowerCase().includes(q));
    }
    return list;
  }, [profileSearch, profileWingFilter]);

  const unacknowledgedCount = useMemo(() => alerts.filter(a => !a.acknowledged).length, [alerts]);

  const filteredAlerts = useMemo(() => {
    if (alertFilter === 'all') return alerts;
    if (alertFilter === 'unread') return alerts.filter(a => !a.acknowledged);
    return alerts.filter(a => a.urgency === alertFilter);
  }, [alerts, alertFilter]);

  const ticketResidents = useMemo(() => {
    if (!ticketSearch.trim()) return MOCK_RESIDENTS;
    const q = ticketSearch.toLowerCase();
    return MOCK_RESIDENTS.filter(r => r.name.toLowerCase().includes(q) || r.room.toLowerCase().includes(q));
  }, [ticketSearch]);

  const tempStats = useMemo(() => {
    const pass = MOCK_TEMP_LOGS.filter(t => t.status === 'pass').length;
    return { total: MOCK_TEMP_LOGS.length, pass, fail: MOCK_TEMP_LOGS.length - pass, rate: Math.round((pass / MOCK_TEMP_LOGS.length) * 100) };
  }, []);

  const cleaningStats = useMemo(() => {
    const done = MOCK_CLEANING.filter(c => c.status === 'done').length;
    const due = MOCK_CLEANING.filter(c => c.status === 'due').length;
    const overdue = MOCK_CLEANING.filter(c => c.status === 'overdue').length;
    return { done, due, overdue, total: MOCK_CLEANING.length };
  }, []);

  const todayKey = new Date().toISOString().split('T')[0];
  const intakeKey = useCallback((residentId, meal) => `${todayKey}_${meal}_${residentId}`, [todayKey]);

  const poorIntakeResidents = useMemo(() => {
    const poor = [];
    for (const r of MOCK_RESIDENTS) {
      for (const meal of MEAL_SLOTS) {
        const key = intakeKey(r.id, meal);
        const entry = intakeData[key];
        if (entry && ['1/4', 'Refused', 'NPO'].includes(entry.level)) {
          poor.push({ ...r, meal, level: entry.level, notes: entry.notes });
        }
      }
    }
    return poor;
  }, [intakeData, intakeKey]);

  const acknowledgeAlert = (id) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, acknowledged: true } : a));
    showToast('Alert acknowledged', 'success');
  };

  const handleMenuSave = () => {
    if (!menuEditCell) return;
    const { day, slot } = menuEditCell;
    setMenuData(prev => ({ ...prev, [day]: { ...prev[day], [slot]: menuEditValue } }));
    setMenuEditCell(null);
    setMenuEditValue('');
    showToast('Menu item saved', 'success');
  };

  const handleIntakeSave = (residentId, meal, level, notes) => {
    const key = intakeKey(residentId, meal);
    const next = { ...intakeData, [key]: { level, notes, savedAt: new Date().toISOString() } };
    setIntakeData(next);
    saveMealIntake(next);
    showToast('Meal intake recorded', 'success');
  };

  const formatTimestamp = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 shadow-lg shadow-purple-200">
            <UtensilsCrossed className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kitchen & Dietary</h1>
            <p className="text-sm text-gray-500">Loading dietary data…</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}
        </div>
        <TableSkeleton rows={5} columns={4} />
      </div>
    );
  }

  // ── Render: Dietary Profiles ───────────────────────────────────
  const renderProfiles = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Residents" value={MOCK_RESIDENTS.length} sub="Dietary profiles loaded" />
        <StatCard icon={CheckCircle} label="Profiles Synced" value={MOCK_RESIDENTS.filter(r => r.syncStatus === 'synced').length} sub="Up to date with clinical" color="green" />
        <StatCard icon={RefreshCw} label="Pending Sync" value={MOCK_RESIDENTS.filter(r => r.syncStatus === 'pending').length} sub="Awaiting data" color="amber" />
        <StatCard icon={AlertTriangle} label="Sync Errors" value={MOCK_RESIDENTS.filter(r => r.syncStatus === 'error').length} sub="Needs attention" color="red" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Resident Dietary Profiles</h3>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search residents…"
                value={profileSearch}
                onChange={e => setProfileSearch(e.target.value)}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none w-56"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={profileWingFilter}
                onChange={e => setProfileWingFilter(e.target.value)}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none appearance-none bg-white"
              >
                <option value="all">All Wings</option>
                <option value="Banksia">Banksia</option>
                <option value="Wattle">Wattle</option>
                <option value="Grevillea">Grevillea</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
              <RefreshCw className="w-4 h-4" /> Sync All
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredResidents.length === 0 && (
            <div className="p-8">
              <EmptyState
                icon={Users}
                title="No residents found"
                description="No residents match the current search or wing filter. Try adjusting your criteria."
              />
            </div>
          )}
          {filteredResidents.map(r => (
            <div key={r.id} className="hover:bg-purple-50/30 transition-colors">
              <button
                onClick={() => setExpandedResident(expandedResident === r.id ? null : r.id)}
                className="w-full px-5 py-4 flex items-center gap-4 text-left"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {r.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900 truncate">{r.name}</p>
                    <SyncBadge status={r.syncStatus} />
                  </div>
                  <p className="text-sm text-gray-500">Room {r.room} · {r.wing} Wing</p>
                </div>
                <div className="hidden md:flex items-center gap-3">
                  <IddsiLabel level={r.foodLevel} type="food" />
                  <IddsiLabel level={r.fluidLevel} type="fluid" />
                  {r.allergies.length > 0 && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                      {r.allergies.length} Allerg{r.allergies.length > 1 ? 'ies' : 'y'}
                    </span>
                  )}
                </div>
                {expandedResident === r.id ? <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" /> : <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />}
              </button>
              {expandedResident === r.id && (
                <div className="px-5 pb-5 pt-0">
                  <div className="ml-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">IDDSI Food Level</p>
                      <Tooltip content={IDDSI_FOOD_TOOLTIPS[r.foodLevel]} position="bottom">
                        <p className={`inline-block px-3 py-1 rounded-lg text-sm font-semibold cursor-help ${IDDSI_FOOD_COLORS[r.foodLevel]}`}>
                          Level {r.foodLevel} — {IDDSI_FOOD_LABELS[r.foodLevel]}
                        </p>
                      </Tooltip>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">IDDSI Fluid Level</p>
                      <Tooltip content={IDDSI_FLUID_TOOLTIPS[r.fluidLevel]} position="bottom">
                        <p className={`inline-block px-3 py-1 rounded-lg text-sm font-semibold cursor-help ${IDDSI_FLUID_COLORS[r.fluidLevel]}`}>
                          Level {r.fluidLevel} — {IDDSI_FLUID_LABELS[r.fluidLevel]}
                        </p>
                      </Tooltip>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Portion Size</p>
                      <p className="text-sm font-medium text-gray-900">{r.portionSize}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Last Assessment</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(r.lastAssessment)}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Allergies</p>
                      {r.allergies.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {r.allergies.map(a => (
                            <span key={a} className="px-2.5 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-semibold flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> {a}
                            </span>
                          ))}
                        </div>
                      ) : <p className="text-sm text-gray-400">None recorded</p>}
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Preferences</p>
                      {r.preferences.length > 0 ? (
                        <ul className="space-y-1">
                          {r.preferences.map((p, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                              <Star className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" /> {p}
                            </li>
                          ))}
                        </ul>
                      ) : <p className="text-sm text-gray-400">None recorded</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Render: Weekly Menu ────────────────────────────────────────
  const renderMenu = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Weekly Menu Planner</h3>
          <p className="text-sm text-gray-500">Week of 7 Apr – 13 Apr 2026</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            <Plus className="w-4 h-4" /> New Template
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
            <ArrowRight className="w-4 h-4" /> Push to Family Portal
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-purple-50 to-purple-100/60">
                <th className="text-left px-4 py-3 font-semibold text-purple-900 border-b border-purple-100 sticky left-0 bg-purple-50 z-10 min-w-[120px]">Meal</th>
                {DAYS.map(d => (
                  <th key={d} className="text-left px-4 py-3 font-semibold text-purple-900 border-b border-purple-100 min-w-[170px]">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {MEAL_SLOTS.map(slot => (
                <tr key={slot} className="hover:bg-purple-50/20">
                  <td className="px-4 py-3 font-medium text-gray-900 border-r border-gray-100 sticky left-0 bg-white z-10">
                    {slot}
                  </td>
                  {DAYS.map(day => {
                    const isEditing = menuEditCell?.day === day && menuEditCell?.slot === slot;
                    const val = menuData[day]?.[slot] || '';
                    return (
                      <td key={day} className="px-4 py-3 text-gray-700 group relative">
                        {isEditing ? (
                          <div className="flex flex-col gap-2">
                            <input
                              autoFocus
                              value={menuEditValue}
                              onChange={e => setMenuEditValue(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleMenuSave(); if (e.key === 'Escape') setMenuEditCell(null); }}
                              className="w-full px-2 py-1 border border-purple-400 rounded text-sm focus:ring-2 focus:ring-purple-300 outline-none"
                            />
                            <div className="flex gap-1">
                              <button onClick={handleMenuSave} className="px-2 py-0.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700">Save</button>
                              <button onClick={() => setMenuEditCell(null)} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <span className="leading-snug">{val}</span>
                            <button
                              onClick={() => { setMenuEditCell({ day, slot }); setMenuEditValue(val); }}
                              className="opacity-0 group-hover:opacity-100 ml-2 p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-all shrink-0"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // ── Render: Meal Tickets ───────────────────────────────────────
  const renderTickets = () => {
    const currentDay = DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Meal Ticket Generation</h3>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search resident…"
                value={ticketSearch}
                onChange={e => setTicketSearch(e.target.value)}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none w-52"
              />
            </div>
            <div className="relative">
              <UtensilsCrossed className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={ticketMeal}
                onChange={e => setTicketMeal(e.target.value)}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none appearance-none bg-white"
              >
                {MEAL_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <button
              onClick={() => { window.print(); showToast('Meal tickets sent to printer', 'info'); }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              <Printer className="w-4 h-4" /> Print Tickets
            </button>
          </div>
        </div>

        {ticketResidents.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
            <EmptyState
              icon={FileText}
              title="No meal tickets"
              description="No residents match your search. Adjust filters to generate tickets."
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {ticketResidents.map(r => {
              const menuItem = menuData[currentDay]?.[ticketMeal] || '—';
              const allergenHits = detectAllergenMatches(r.allergies, menuItem);
              return (
                <div key={r.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid">
                  <div className={`px-5 py-3 flex items-center justify-between ${IDDSI_FOOD_COLORS[r.foodLevel].replace('text-', 'bg-').split(' ')[0]}`}>
                    <div>
                      <p className="font-bold text-gray-900">{r.name}</p>
                      <p className="text-xs text-gray-600">Room {r.room} · {r.wing}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">{ticketMeal}</p>
                      <p className="text-xs text-gray-500">{formatDate(new Date().toISOString())}</p>
                    </div>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <IddsiLabel level={r.foodLevel} type="food" />
                      <IddsiLabel level={r.fluidLevel} type="fluid" />
                      <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700">
                        {r.portionSize}
                      </span>
                    </div>

                    {r.allergies.length > 0 && (
                      <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> Allergies
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {r.allergies.map(a => (
                            <span key={a} className="px-2 py-0.5 bg-red-200 text-red-800 rounded text-xs font-semibold">{a}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {allergenHits.length > 0 && (
                      <div className="p-3 bg-red-600 rounded-lg shadow-md animate-pulse">
                        <p className="text-xs font-black text-white uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                          <AlertTriangle className="w-4 h-4" /> ALLERGY WARNING
                        </p>
                        {allergenHits.map((hit, i) => (
                          <p key={i} className="text-sm font-semibold text-red-100">
                            {hit.allergen} detected in "{menuItem}"
                          </p>
                        ))}
                      </div>
                    )}

                    {r.preferences.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Preferences</p>
                        <p className="text-sm text-gray-700">{r.preferences.join(' · ')}</p>
                      </div>
                    )}

                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 mb-0.5">Today's Menu Item</p>
                      <p className="text-sm font-medium text-gray-900">{menuItem}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Render: Meal Intake Recording ──────────────────────────────
  const renderIntake = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Meal Intake Recording</h3>
          <p className="text-sm text-gray-500">{formatDate(new Date().toISOString())} — track what residents consumed</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
            <button
              onClick={() => setIntakeView('record')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${intakeView === 'record' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Record
            </button>
            <button
              onClick={() => setIntakeView('summary')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${intakeView === 'summary' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Poor Intake Summary
            </button>
          </div>
          <div className="relative">
            <UtensilsCrossed className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={intakeMeal}
              onChange={e => setIntakeMeal(e.target.value)}
              className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none appearance-none bg-white"
            >
              {MEAL_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {intakeView === 'record' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-purple-50 to-purple-100/60">
                  <th className="text-left px-4 py-3 font-semibold text-purple-900 border-b border-purple-100">Resident</th>
                  <th className="text-left px-4 py-3 font-semibold text-purple-900 border-b border-purple-100">Room</th>
                  <th className="text-left px-4 py-3 font-semibold text-purple-900 border-b border-purple-100">IDDSI</th>
                  <th className="text-left px-4 py-3 font-semibold text-purple-900 border-b border-purple-100 min-w-[140px]">Intake Level</th>
                  <th className="text-left px-4 py-3 font-semibold text-purple-900 border-b border-purple-100 min-w-[200px]">Notes</th>
                  <th className="text-left px-4 py-3 font-semibold text-purple-900 border-b border-purple-100 w-20">Save</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {MOCK_RESIDENTS.map(r => {
                  const key = intakeKey(r.id, intakeMeal);
                  const saved = intakeData[key];
                  return (
                    <IntakeRow key={r.id} resident={r} savedEntry={saved} meal={intakeMeal} onSave={handleIntakeSave} />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {intakeView === 'summary' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={BarChart3} label="Poor Intake Entries" value={poorIntakeResidents.length} sub="< 1/2 or Refused/NPO today" color="red" />
            <StatCard icon={Users} label="Total Residents" value={MOCK_RESIDENTS.length} sub="Across all wings" />
            <StatCard icon={CheckCircle} label="Adequate Intake" value={MOCK_RESIDENTS.length - poorIntakeResidents.length} sub="≥ 1/2 or not yet recorded" color="green" />
          </div>

          {poorIntakeResidents.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
              <EmptyState
                icon={CheckCircle}
                title="No poor intake flagged"
                description="All recorded residents have adequate intake today, or no intake data has been entered yet."
              />
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100">
                <h4 className="text-base font-semibold text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Residents with poor intake — requires clinical review
                </h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-red-50 text-left">
                      <th className="px-4 py-3 font-semibold text-red-800">Resident</th>
                      <th className="px-4 py-3 font-semibold text-red-800">Room</th>
                      <th className="px-4 py-3 font-semibold text-red-800">Meal</th>
                      <th className="px-4 py-3 font-semibold text-red-800">Intake</th>
                      <th className="px-4 py-3 font-semibold text-red-800">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {poorIntakeResidents.map((r, i) => (
                      <tr key={`${r.id}-${r.meal}-${i}`} className={r.level === 'Refused' || r.level === 'NPO' ? 'bg-red-50/50' : 'bg-amber-50/30'}>
                        <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                        <td className="px-4 py-3 text-gray-600">{r.room}</td>
                        <td className="px-4 py-3 text-gray-600">{r.meal}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            r.level === 'Refused' || r.level === 'NPO' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'
                          }`}>
                            {r.level}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{r.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── Render: Change Alerts ──────────────────────────────────────
  const renderAlerts = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Bell} label="Total Alerts" value={alerts.length} sub="Last 7 days" />
        <StatCard icon={AlertTriangle} label="Unacknowledged" value={unacknowledgedCount} sub="Require action" color="red" />
        <StatCard icon={TrendingUp} label="IDDSI Changes" value={alerts.filter(a => a.changeType.includes('IDDSI')).length} sub="Texture modifications" color="amber" />
        <StatCard icon={Shield} label="New Allergies" value={alerts.filter(a => a.changeType.includes('Allergy')).length} sub="Critical updates" color="red" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Dietary Change Alerts</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {['all', 'unread', 'critical', 'high', 'medium', 'low'].map(f => (
              <button
                key={f}
                onClick={() => setAlertFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${alertFilter === f ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {f === 'all' ? 'All' : f === 'unread' ? `Unread (${unacknowledgedCount})` : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredAlerts.length === 0 && (
            <div className="p-8">
              <EmptyState
                icon={Bell}
                title="No alerts"
                description="No dietary change alerts match this filter."
              />
            </div>
          )}
          {filteredAlerts.map(a => (
            <div key={a.id} className={`px-5 py-4 flex items-start gap-4 ${!a.acknowledged ? 'bg-purple-50/40' : ''}`}>
              <div className={`mt-1 p-2 rounded-xl shrink-0 ${a.urgency === 'critical' ? 'bg-red-100' : a.urgency === 'high' ? 'bg-orange-100' : a.urgency === 'medium' ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                {a.changeType.includes('Allergy') ? <AlertTriangle className={`w-5 h-5 ${a.urgency === 'critical' ? 'text-red-600' : 'text-orange-500'}`} /> : <UtensilsCrossed className={`w-5 h-5 ${a.urgency === 'high' ? 'text-orange-500' : 'text-gray-500'}`} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="font-semibold text-gray-900">{a.residentName}</p>
                  <span className="text-xs text-gray-400">Room {a.room}</span>
                  <UrgencyBadge level={a.urgency} />
                  {!a.acknowledged && <span className="w-2 h-2 bg-purple-500 rounded-full" />}
                </div>
                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">{a.changeType}:</span>{' '}
                  {a.oldValue !== '—' && <><span className="line-through text-gray-400">{a.oldValue}</span> <ArrowRight className="inline w-3.5 h-3.5 text-gray-400" /> </>}
                  <span className="font-semibold text-gray-900">{a.newValue}</span>
                </p>
                <p className="text-xs text-gray-500">Changed by {a.changedBy} · {formatTimestamp(a.timestamp)}</p>
              </div>
              {!a.acknowledged && (
                <button
                  onClick={() => acknowledgeAlert(a.id)}
                  className="shrink-0 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 transition-colors"
                >
                  Acknowledge
                </button>
              )}
              {a.acknowledged && (
                <span className="shrink-0 flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="w-4 h-4" /> Acknowledged
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Render: Food Safety ────────────────────────────────────────
  const renderSafety = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Thermometer} label="Temp Compliance" value={`${tempStats.rate}%`} sub={`${tempStats.pass}/${tempStats.total} checks passed`} color={tempStats.rate >= 80 ? 'green' : 'red'} />
        <StatCard icon={XCircle} label="Temp Failures" value={tempStats.fail} sub="Require corrective action" color="red" />
        <StatCard icon={ClipboardCheck} label="Cleaning Done" value={cleaningStats.done} sub={`of ${cleaningStats.total} tasks`} color="green" />
        <StatCard icon={AlertTriangle} label="Overdue Tasks" value={cleaningStats.overdue} sub={`${cleaningStats.due} upcoming`} color="red" />
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'temps', label: 'Temperature Logs', icon: Thermometer },
          { key: 'cleaning', label: 'Cleaning Schedule', icon: ClipboardCheck },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSafetySubTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${safetySubTab === t.key ? 'bg-purple-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {safetySubTab === 'temps' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Temperature Records — {formatDate(new Date().toISOString())}</h3>
            <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
              <Plus className="w-4 h-4" /> Log Temperature
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600">Equipment</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Type</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Temp (°C)</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Target</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Recorded By</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {MOCK_TEMP_LOGS.map(t => (
                  <tr key={t.id} className={t.status === 'fail' ? 'bg-red-50/50' : 'hover:bg-gray-50/50'}>
                    <td className="px-4 py-3 font-medium text-gray-900">{t.equipment}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-gray-600">
                        {t.type === 'fridge' && <Snowflake className="w-3.5 h-3.5 text-blue-500" />}
                        {t.type === 'freezer' && <Snowflake className="w-3.5 h-3.5 text-indigo-500" />}
                        {t.type === 'hot' && <Flame className="w-3.5 h-3.5 text-red-500" />}
                        {t.type === 'cold' && <Droplets className="w-3.5 h-3.5 text-cyan-500" />}
                        <span className="capitalize">{t.type}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono font-semibold text-gray-900">{t.temp.toFixed(1)}</td>
                    <td className="px-4 py-3 text-gray-500">{t.target}</td>
                    <td className="px-4 py-3">
                      {t.status === 'pass' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                          <CheckCircle className="w-3 h-3" /> Pass
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                          <XCircle className="w-3 h-3" /> Fail
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{t.recordedBy}</td>
                    <td className="px-4 py-3 text-gray-500">{formatTimestamp(t.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {safetySubTab === 'cleaning' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Cleaning & Maintenance Schedule</h3>
            <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
              <Plus className="w-4 h-4" /> Add Task
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600">Task</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Frequency</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Last Completed</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Completed By</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {MOCK_CLEANING.map(c => (
                  <tr key={c.id} className={c.status === 'overdue' ? 'bg-red-50/50' : c.status === 'due' ? 'bg-amber-50/30' : 'hover:bg-gray-50/50'}>
                    <td className="px-4 py-3 font-medium text-gray-900">{c.task}</td>
                    <td className="px-4 py-3 text-gray-600">{c.frequency}</td>
                    <td className="px-4 py-3 text-gray-600">{formatTimestamp(c.lastCompleted)}</td>
                    <td className="px-4 py-3 text-gray-600">{c.completedBy}</td>
                    <td className="px-4 py-3">
                      {c.status === 'done' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                          <CheckCircle className="w-3 h-3" /> Done
                        </span>
                      )}
                      {c.status === 'due' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                          <Clock className="w-3 h-3" /> Due
                        </span>
                      )}
                      {c.status === 'overdue' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                          <AlertTriangle className="w-3 h-3" /> Overdue
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // ── Main Render ────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 shadow-lg shadow-purple-200">
            <UtensilsCrossed className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kitchen & Dietary</h1>
            <p className="text-sm text-gray-500">Dietary management, menu planning & food safety</p>
          </div>
        </div>
        {unacknowledgedCount > 0 && (
          <button
            onClick={() => setActiveTab('alerts')}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm font-medium hover:bg-red-100 transition-colors"
          >
            <Bell className="w-4 h-4" />
            {unacknowledgedCount} unacknowledged alert{unacknowledgedCount > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.key === 'alerts' && unacknowledgedCount > 0 && (
                <span className="ml-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                  {unacknowledgedCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'profiles' && renderProfiles()}
      {activeTab === 'menu' && renderMenu()}
      {activeTab === 'tickets' && renderTickets()}
      {activeTab === 'intake' && renderIntake()}
      {activeTab === 'alerts' && renderAlerts()}
      {activeTab === 'safety' && renderSafety()}
    </div>
  );
};

// ── Intake Row (isolated state per resident) ─────────────────────
function IntakeRow({ resident: r, savedEntry, meal, onSave }) {
  const [level, setLevel] = useState(savedEntry?.level || '');
  const [notes, setNotes] = useState(savedEntry?.notes || '');
  const dirty = level !== (savedEntry?.level || '') || notes !== (savedEntry?.notes || '');

  useEffect(() => {
    setLevel(savedEntry?.level || '');
    setNotes(savedEntry?.notes || '');
  }, [savedEntry, meal]);

  return (
    <tr className={savedEntry ? 'bg-green-50/30' : 'hover:bg-gray-50/30'}>
      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
      <td className="px-4 py-3 text-gray-600">{r.room} · {r.wing}</td>
      <td className="px-4 py-3">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${IDDSI_FOOD_COLORS[r.foodLevel]}`}>
          {r.foodLevel}
        </span>
      </td>
      <td className="px-4 py-3">
        <select
          value={level}
          onChange={e => setLevel(e.target.value)}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none bg-white"
        >
          <option value="">Select…</option>
          {INTAKE_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </td>
      <td className="px-4 py-3">
        <input
          type="text"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Optional notes…"
          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none"
        />
      </td>
      <td className="px-4 py-3">
        <button
          onClick={() => { if (level) onSave(r.id, meal, level, notes); }}
          disabled={!level || !dirty}
          className={`p-2 rounded-lg transition-colors ${level && dirty ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
          title="Save intake"
        >
          <Save className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

export default Kitchen;
